import {
  HTMLProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  RowData,
  SortingState,
  Table,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as Styled from '../TableStyled';
import TableConfig from '../TableConfig';
import { useHeaderButtons } from '../../header/HeaderButtonProvider';
import SquareButton from '../../button/SquareButton';

import styled, { css, useTheme } from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  completeAllWithinPeriod,
  completeOrderGetApi,
  keyMapping,
  orderDeleteApi,
  orderListGetApi,
  orderListPostApi,
  orderListWithParamApi,
  OrderPaginationRequestParamType,
  OrderPaginationWrapperType,
  OrderPreparationIndexType,
  OrderStateResponseType,
  OrderUploadResponseType,
  PreparationPutApi,
} from '../../../api/apiConfig';
import useFetch from '../../../api/useFetch';
import { OrderUploadModal } from '../../common/modal/ModalComponents/OrderUploadModal';
//주문번호 예시 20241104-1

import { BsArrowDown, BsArrowDownUp, BsArrowUp } from 'react-icons/bs';

import {
  OrderListType,
  OrderListUpdateRequestDto,
} from '../../../api/apiConfig';
import { Pagination, PaginationState } from '../Pagination';
import {
  formatCurrency,
  formatDashFormToEightDigitString,
  formatEightDigitStringToSlashForm,
  getMonthStartEnd,
  removeDashFromString,
} from '../../../function/replaceString';
import { groupOrderWithItemSubrow } from '../../../function/initData';

import {
  adjustEndDate,
  calculateMonthsDifference,
  isValidRange,
} from '../../../function/dateVerify';
import { InputUnderline } from '../../input/InputUnderline';
import {
  PaginationOnServer,
  PaginationOnServerState,
} from '../PaginationOnServer';
import useModal from '../../../hook/useModal';

declare module '@tanstack/react-table' {
  //allows us to define custom properties for our columns
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select';
  }
}

function mapKey(inputKey: string): keyof OrderListType | undefined {
  return keyMapping[inputKey];
}

export const OrderTable = () => {
  const theme = useTheme();
  const { setButtons } = useHeaderButtons();

  const { openAlertModal, openConfirmModal } = useModal();

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [periodStartDate, setPeriodStartDate] = useState<string>(
    getMonthStartEnd(new Date()).startDash
  );

  const [periodEndDate, setPeriodEndDate] = useState<string>(
    getMonthStartEnd(new Date()).endDash
  );

  //구버전 주문조회
  const { fetch: apiGetOrderList } = useFetch({
    fetchFunction: orderListGetApi,
    onSuccess: (data: OrderListType[]) => setData(data),
  });

  /** 주문 삭제 */
  const { fetch: fetchOrderDeleteApi } = useFetch({
    fetchFunction: orderDeleteApi,
    onSuccess: (orderId: string) => deleteOrderCallback(orderId),
  });

  //페이지네이션 및 검색조건 적용된 주문조회
  const { fetch: fetchOrderListWithParamApi, loading } = useFetch({
    fetchFunction: orderListWithParamApi,
    onSuccess: (data: OrderPaginationWrapperType) => {
      setData(data.content);
      setPagination({
        pageIndex: data.pageable.pageNumber + 1,
        pageSize: data.pageable.pageSize,
      });
      setTotalPage(data.totalPages);
      paginationRef.current = {
        pageSize: data.pageable.pageSize,
        pageIndex: data.pageable.pageNumber + 1,
        totalPage: data.totalPages,
      };
    },
  });

  const { fetch: apiPostOrderList } = useFetch({
    fetchFunction: orderListPostApi,
    onSuccess: (data: OrderUploadResponseType) => {
      setUpdatedData(data);
      // toast('업데이트에 성공하였습니다.');
      // setData(data);
    },
  });

  const { fetch: fetchCompleteOrderGetApi } = useFetch({
    fetchFunction: completeOrderGetApi,
    onSuccess: (data: OrderStateResponseType) => completeOrderDataState(data),
  });

  const { fetch: PreparationPut } = useFetch({
    fetchFunction: PreparationPutApi,
    onSuccess: (data: OrderPreparationIndexType) => {
      preparationItemClickHandler(data);
    },
  });

  //현재 조회기간만큼의 주문/주문품목 완료/준비완료 상태로 변경
  const { fetch: fetchCompleteAllWithinPeriod } = useFetch({
    fetchFunction: completeAllWithinPeriod,
    onSuccess: () => getOrderList(),
  });

  function completeOrderDataState(order: OrderStateResponseType) {
    setData((prev) =>
      prev.map((prevOrder) =>
        prevOrder.ORDER_ID === order.ORDER_ID
          ? {
              ...prevOrder,
              state: order.state,
            }
          : prevOrder
      )
    );
  }

  function deleteOrderCallback(orderId: string) {
    setData((prev) =>
      prev.filter((prevOrder) => prevOrder.ORDER_ID !== orderId)
    );
  }

  const [sorting, setSorting] = useState<SortingState>([]);

  const [excludeCompletedOrders, setExcludeCompletedOrders] =
    useState<boolean>(false);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [pagination, setPagination] = useState<PaginationOnServerState>({
    pageIndex: 1,
    pageSize: 50,
  });

  const paginationRef = useRef<{
    pageIndex: number;
    pageSize: number;
    totalPage: number;
  } | null>(null);

  const [totalPage, setTotalPage] = useState<number>(1);

  const [showModal, setShowModal] = useState<boolean>(false);

  const [modalType, setModalType] = useState<'edit' | 'add' | null>(null);

  //모든 테이블값
  const [data, setData] = useState<OrderListType[]>([]);
  //엑셀 추출값
  const [excelData, setExcelData] = useState<OrderListType[]>([]);
  //업데이트 리스폰스로서 하이라이트용도
  const [updatedData, setUpdatedData] =
    useState<OrderUploadResponseType | null>(null);

  //테이블 옵션값 ~~~
  const [headerOverflowType, setHeaderOverflowType] = useState<'wrap' | 'hide'>(
    'hide'
  );
  const [bodyOverflowType, setBodyOverflowType] = useState<'wrap' | 'hide'>(
    'hide'
  );
  const [bodyAlignHoriType, setBodyAlignHoriType] = useState<
    'left' | 'center' | 'right'
  >('left');

  const [cellHoriPaddValue, setCellHoriPaddValue] = useState<number>(5);
  const [bodyVertPaddValue, setBodyVertPaddValue] = useState<number>(5);

  const [showConfig, setShowConfig] = useState<boolean>(false);

  useEffect(() => {
    if (!updatedData) return;
    const changedOrderSN = updatedData.updatedOrInsertedOrders
      .map((order) => order.ORDER_ID)
      .filter((sn) => sn !== undefined);
    let newData: OrderListType[] = data.filter(
      (prev) => !changedOrderSN.includes(prev.ORDER_ID!)
    );

    setData([...updatedData.updatedOrInsertedOrders, ...newData]);
  }, [updatedData]);

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='조회하기'
          colorType={'ACTIVE'}
          onClick={() => getOrderList()}
        />
        <SquareButton
          text='일괄확인처리'
          colorType={'ACTIVE'}
          onClick={() => {
            openConfirmModal(
              `현재 조회기간동안의 주문을 일괄적으로 완료 처리합니다.\n(완료처리된 주문은 [미배정 주문 목록]에 더이상 보이지 않습니다.)`,
              () => {
                const startDt = removeDashFromString(periodStartDate);
                const endDt = removeDashFromString(periodEndDate);
                fetchCompleteAllWithinPeriod({
                  startDt,
                  endDt,
                });
              }
            );
          }}
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [setButtons, periodStartDate, periodEndDate, searchTerm, pagination]);

  function getOrderList() {
    if (
      !periodStartDate ||
      !periodEndDate ||
      !pagination.pageIndex ||
      !pagination.pageSize
    )
      return;
    const { pageIndex, pageSize } = pagination;
    const startDt = formatDashFormToEightDigitString(periodStartDate);
    const endDt = formatDashFormToEightDigitString(periodEndDate);

    if (startDt == null || endDt == null) return;
    const dto: OrderPaginationRequestParamType = {
      page: pageIndex,
      size: pageSize,
      startDt: startDt,
      endDt: endDt,
      search: searchTerm,
      completeHidden: excludeCompletedOrders,
    };
    fetchOrderListWithParamApi(dto);
  }

  useEffect(() => {
    getOrderList();
  }, []);

  useEffect(() => {
    if (
      paginationRef.current !== null &&
      paginationRef.current.pageIndex === pagination.pageIndex &&
      paginationRef.current.pageSize === pagination.pageSize &&
      paginationRef.current.totalPage === totalPage
    ) {
      return;
    }

    getOrderList();

    //테이블 표시 수량 변경
    table.setState((prev) => ({
      ...prev,
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: 0,
      },
    }));
  }, [pagination]);

  const PreparationCell = ({
    textContent,
    rowId,
    subRowId,
    preparationState,
  }: {
    textContent: string;
    rowId: number;
    subRowId: number;
    preparationState?: string;
  }) => {
    const [openDropdown, setOpenDropdown] = useState<boolean>(false);

    const clickable = textContent !== '완료';

    return (
      <Styled.PreparationCell
        preparation={preparationState}
        $clickable={clickable}
        onClick={() => {
          clickable && setOpenDropdown(!openDropdown);
        }}
      >
        {textContent}
        <PreparationDropdownBox
          open={openDropdown}
          rowId={rowId}
          subRowId={subRowId}
          current={textContent}
          setOpen={(bool: boolean) => setOpenDropdown(bool)}
        />
      </Styled.PreparationCell>
    );
  };

  const PreparationDropdownBox = ({
    rowId,
    subRowId,
    current,
    open,
    setOpen,
  }: {
    rowId: number;
    subRowId: number;
    current: string;
    open: boolean;
    setOpen: (bool: boolean) => void;
  }) => {
    const preparationStatus = ['준비중', '준비완료'];
    // let open = false;
    return (
      <DropdownBase $show={open}>
        {preparationStatus.map((status) => (
          <DropdownItem
            onClick={() => {
              PreparationPut({
                id: subRowId,
                preparationState: status,
                rowId: rowId,
              });
              setOpen(false);
            }}
            included={current === status}
            key={status}
          >
            {status}
          </DropdownItem>
        ))}
      </DropdownBase>
    );
  };

  const columns = useMemo<ColumnDef<OrderListType>[]>(
    () => [
      {
        //TH Cell
        header: ({ table }) => <>No</>,
        cell: ({ row, cell, getValue }) => {
          const isParent = row.getCanExpand();
          let open = false;
          let isCompleted: boolean = false;
          let textContent: string;
          let isCompletable: boolean = false;
          let hoverText: string | null = null;
          if (isParent) {
            isCompletable = !row.subRows.some(
              (subrow) => subrow.original.preparationState !== '준비완료'
            );
          }

          if (isParent) {
            isCompleted = row.original.state === 2 || false;
            hoverText = isCompleted ? '완료해제' : '완료하기';

            const indexOffset =
              (pagination.pageIndex - 1) * pagination.pageSize;
            textContent = (indexOffset + row.index + 1).toString();
          } else {
            isCompleted = row.getParentRow()?.original.state === 2 || false;
            textContent = isCompleted
              ? '완료'
              : row.original.preparationState || '';
          }

          let preparationState = isCompleted
            ? '완료'
            : !isParent
            ? row.original.preparationState
            : undefined;

          return isParent ? (
            <Styled.NoBodyCell
              style={{ paddingLeft: `${row.depth * 2}rem` }}
              $completable={isCompletable || isCompleted}
              onClick={() =>
                (isCompletable || isCompleted) &&
                fetchCompleteOrderGetApi(row.original.ORDER_ID)
              }
              onMouseEnter={(e) => {
                if (isCompleted || isCompletable)
                  e.currentTarget.textContent = hoverText || ''; // hoverText로 변경
              }}
              onMouseLeave={(e) => {
                if (isCompleted || isCompletable)
                  e.currentTarget.textContent = textContent; // 원래 textContent로 복구
              }}
            >
              {textContent}
            </Styled.NoBodyCell>
          ) : (
            <PreparationCell
              preparationState={preparationState}
              rowId={row.getParentRow()?.original.orderId!}
              subRowId={row.original.id!}
              textContent={textContent}
            />
          );
        },

        id: 'NO',
        size: 79,
      },
      {
        header: '주문번호',
        accessorKey: 'ORDER_ID',
        size: 84,
      },
      {
        header: '거래처명',
        accessorKey: 'CUST_DES',
        size: 180,
      },
      {
        header: '담당자명',
        accessorKey: 'EMP_NAME',
        size: 65,
      },
      {
        header: '품목명',
        accessorKey: 'PROD_DES',
        size: 140,
      },
      {
        header: '규격',
        accessorKey: 'SIZE_DES',
        size: 140,
      },
      {
        header: '수량',
        accessorKey: 'QTY',
        size: 48,
      },
      {
        header: '단가',
        accessorKey: 'PRICE',
        size: 53,
        cell: (price) => formatCurrency(price.getValue() as number),
      },
      {
        header: '공급가액',
        accessorKey: 'SUPPLY_AMT',
        size: 73,
        cell: (supplyAmt) => formatCurrency(supplyAmt.getValue() as number),
      },
      {
        header: '납기일자',
        accessorKey: 'TIME_DATE',
        size: 140,
        cell: (timeDate) =>
          formatEightDigitStringToSlashForm(timeDate.getValue() as string),
      },
      {
        header: '주문방법',
        accessorKey: 'ORDER_TYPE',
        size: 66,
      },
      {
        header: '배송방법',
        accessorKey: 'DELIVERY_TYPE',
        size: 66,
      },
      {
        header: '주문특이사항',
        accessorKey: 'DESCRIPTION',
        size: 420,
      },
      {
        header: '적요',
        accessorKey: 'REMARKS',
        size: 420,
      },
      {
        header: '삭제',
        size: 58,
        cell: ({ row }) => {
          const isParent = row.getCanExpand();
          if (!isParent) return;
          const orderId = row.original.ORDER_ID;
          if (orderId == null) return;
          return (
            <div
              style={{
                width: '100%',
                textAlign: 'center',
                color: 'red',
                cursor: 'pointer',
              }}
              onClick={() => fetchOrderDeleteApi(orderId)}
            >
              삭제
            </div>
          );
        },
      },
    ],
    [pagination]
  );

  //아마 페이지네이션
  function useSkipper() {
    const shouldSkipRef = useRef(true);
    const shouldSkip = shouldSkipRef.current;

    // Wrap a function with this to skip a pagination reset temporarily
    const skip = useCallback(() => {
      shouldSkipRef.current = false;
    }, []);

    useEffect(() => {
      shouldSkipRef.current = true;
    });

    return [shouldSkip, skip] as const;
  }

  async function post(data: OrderListUpdateRequestDto) {
    const sortedOrderList = await groupOrderWithItemSubrow(data.orderList);
    if (sortedOrderList) {
      const sortedData = {
        startDt: data.startDt,
        endDt: data.endDt,
        orderList: await groupOrderWithItemSubrow(data.orderList),
      };
      apiPostOrderList(sortedData);
    }
  }

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();
  //~~페이지네이션

  const customFilter = (table: Table<OrderListType>) => {
    console.log('props', table);
  };

  function preparationItemClickHandler(newData: OrderPreparationIndexType) {
    const { rowId, subRowId, preparationState } = newData;

    setData((prev) =>
      prev.map((order) =>
        order.orderId === rowId
          ? {
              ...order,
              subRows: order.subRows?.map((subRow) =>
                subRow.id === subRowId
                  ? {
                      ...subRow,
                      preparationState: preparationState,
                    }
                  : subRow
              ),
            }
          : order
      )
    );
  }

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    state: {
      expanded: true,
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: 0,
      },
      sorting,
      // columnFilters,
    },
    getSubRows: (row) => row.subRows,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    autoResetPageIndex,
    autoResetExpanded: false,
    getPaginationRowModel: getPaginationRowModel(),
    paginateExpandedRows: false,
    onSortingChange: setSorting,
  });

  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }
    return colSizes;
  }, [table.getState().columnSizingInfo, table.getState().columnSizing]);

  // 시작일 변경 처리
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;

    if (periodEndDate && !isValidRange(newStartDate, periodEndDate)) {
      return;
    }

    setPeriodStartDate(newStartDate);

    if (periodEndDate) {
      // 시작일 변경 시 종료일 조정
      const monthsDiff = calculateMonthsDifference(newStartDate, periodEndDate);
      if (monthsDiff >= 12) {
        // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
        const adjustedEndDate = adjustEndDate(newStartDate);
        const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
        setPeriodEndDate(newEndDateStr);
        toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
      }
    }
  };

  // 종료일 변경 처리
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;

    if (periodStartDate && !isValidRange(periodStartDate, newEndDate)) {
      return;
    }

    setPeriodEndDate(newEndDate);

    // 종료일 변경 시 월 수가 12개월을 넘지 않도록 조정
    const monthsDiff = calculateMonthsDifference(periodStartDate, newEndDate);
    if (monthsDiff >= 12) {
      // 종료일이 12개월을 초과하는 경우, 종료일을 12개월 내로 조정
      const adjustedEndDate = adjustEndDate(periodStartDate);
      const newEndDateStr = adjustedEndDate.toISOString().split('T')[0];
      setPeriodEndDate(newEndDateStr);
      toast.error('기간이 12개월을 초과하여 자동으로 조정되었습니다');
    }
  };

  return (
    <>
      <PeriodContainer>
        <PeriodRow>
          <RowLabel>조회시작일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodStartDate}
              onChange={handleStartDateChange}
            />
          </RowContent>
          <RowLabel>조회종료일</RowLabel>
          <RowContent>
            <DateInput
              type='date'
              value={periodEndDate}
              onChange={handleEndDateChange}
            />
          </RowContent>
        </PeriodRow>
        <PeriodRow>
          <RowLabel>완료된 주문 숨김</RowLabel>
          <RowContent>
            <input
              type='checkbox'
              checked={excludeCompletedOrders}
              onChange={(e) => setExcludeCompletedOrders(e.target.checked)}
              style={{ width: '17px', height: '17px' }}
            />
          </RowContent>
        </PeriodRow>
        <PeriodRow>
          <RowLabel>검색어</RowLabel>
          <RowContent>
            <InputUnderline
              placeHolder={'주문번호 및 거래처명에서 검색됩니다'}
              style={{ width: '260px' }}
              value={searchTerm}
              onChange={function (text: string): void {
                setSearchTerm(text);
              }}
              onEnter={(text) => getOrderList()}
            />
          </RowContent>
        </PeriodRow>
      </PeriodContainer>

      <PaginationOnServer
        pagination={pagination}
        setPagination={setPagination}
        totalPage={totalPage}
        loading={loading}
      />

      {showModal && modalType && (
        <OrderUploadModal
          onClose={() => {
            setShowModal(false);
          }}
          onSelect={(item: OrderListUpdateRequestDto) => post(item)}
          mode='add'
        />
      )}
      <Styled.TableContainer>
        <Styled.TableOptionButton onClick={() => setShowConfig(!showConfig)}>
          설정
        </Styled.TableOptionButton>
        <TableConfig
          show={showConfig}
          Header_Overflow_Type={headerOverflowType}
          setHeaderOverflowType={(value: 'hide' | 'wrap') =>
            setHeaderOverflowType(value)
          }
          Body_Overflow_Type={bodyOverflowType}
          setBodyOverflowType={(value: 'hide' | 'wrap') =>
            setBodyOverflowType(value)
          }
          Body_Align_Hori_Type={bodyAlignHoriType}
          setBodyAlignHoriType={(value: 'left' | 'center' | 'right') =>
            setBodyAlignHoriType(value)
          }
          Cell_Hori_Padd_Value={cellHoriPaddValue}
          setCellHoriPaddValue={(value: number) => setCellHoriPaddValue(value)}
          Body_Vert_Padd_Value={bodyVertPaddValue}
          setBodyVertPaddValue={(value: number) => setBodyVertPaddValue(value)}
        />
        <Styled.TableDiv
          {...{
            style: {
              ...columnSizeVars,
              width: table.getTotalSize(),
            },
          }}
          $headOverflowType={headerOverflowType}
          $bodyOverflowType={bodyOverflowType}
          $bodyAlignHoriType={bodyAlignHoriType}
          $cellHoriPaddValue={cellHoriPaddValue}
          $bodyVertPaddValue={bodyVertPaddValue}
        >
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <Styled.ThDiv key={headerGroup.id}>
                {headerGroup.headers.map((headerCell) =>
                  headerCell.isPlaceholder ? null : (
                    <Styled.ThCell
                      $disActivePad={headerCell.column.id === 'NO'}
                      key={headerCell.id}
                      {...{
                        style: {
                          width: `calc(var(--header-${headerCell?.id}-size) * 1px)`,
                          cursor: headerCell.column.getCanSort()
                            ? 'pointer'
                            : 'auto',
                        },
                        onClick: headerCell.column.getToggleSortingHandler(),
                        className: 'th',
                      }}
                    >
                      {headerCell.isPlaceholder
                        ? null
                        : flexRender(
                            headerCell.column.columnDef.header,
                            headerCell.getContext()
                          )}
                      {headerCell.column.getIsSorted() === 'asc' && (
                        <BsArrowDown size={14} style={{ marginLeft: '5px' }} />
                      )}
                      {headerCell.column.getIsSorted() === 'desc' && (
                        <BsArrowUp size={14} style={{ marginLeft: '5px' }} />
                      )}
                      {!headerCell.column.getIsSorted() &&
                        headerCell.column.getCanSort() && (
                          <BsArrowDownUp
                            size={14}
                            style={{ marginLeft: '5px' }}
                          />
                        )}
                      {/* {headerCell.column.getCanFilter() ? (
                        <div>
                          <TableFilter column={headerCell.column} />
                        </div>
                      ) : null} */}
                      <Styled.ResizeHandler
                        {...{
                          onMouseDown: headerCell.getResizeHandler(),
                          className: `resizer ${
                            headerCell.column.getIsResizing()
                              ? 'isResizing'
                              : ''
                          }`,
                        }}
                      />
                    </Styled.ThCell>
                  )
                )}
              </Styled.ThDiv>
            );
          })}
          {table
            .getRowModel()
            .rows.filter((row) => {
              if (!excludeCompletedOrders) return true;
              const isParent = row.getCanExpand();

              if (isParent) {
                if (row.original.state === 2) {
                  return false;
                } else {
                  return true;
                }
              } else {
                if (row.getParentRow()?.original.state === 2) {
                  return false;
                } else {
                  return true;
                }
              }
            })
            .map((row) => (
              <>
                <Styled.TdDiv
                  key={row.id}
                  {...{
                    style: {
                      // 전체 배경 색 변경 (변경된 필드가 있으면)
                      backgroundColor:
                        (row.original.changedOrderFields &&
                          row.original.changedOrderFields.length > 0) ||
                        row.original.subRows?.some(
                          (subRow) =>
                            subRow.changedItemFields &&
                            subRow.changedItemFields?.length > 0
                        )
                          ? theme.colors.lime
                          : 'transparent',
                    },
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isMainRow = row.depth === 0;

                    // 서브로우에서 각 셀의 변경된 필드 확인 (셀이 속한 서브로우마다)
                    let subRowChangedFields = null;
                    if (!isMainRow) {
                      // 메인로우가 아닐 경우, 자기 자신에서만 변경된 필드를 찾음
                      subRowChangedFields = row.original.changedItemFields
                        ? row.original.changedItemFields.find(
                            (field) => mapKey(field) === cell.column.id
                          )
                        : null;
                    } else {
                      // 서브로우에서 찾는 경우
                      subRowChangedFields = row.original.subRows
                        ? row.original.subRows.reduce(
                            (acc, subRow, subRowIndex) => {
                              if (subRow.changedItemFields) {
                                if (
                                  subRow.changedItemFields.find(
                                    (field) => mapKey(field) === cell.column.id
                                  )
                                ) {
                                  acc[subRowIndex] = true; // 해당 서브로우의 셀이 변경되었음을 표시
                                }
                              }
                              return acc;
                            },
                            {} as Record<number, boolean>
                          )
                        : null;
                    }

                    // 메인로우에서 변경된 필드 확인 (주문 필드 변경)
                    const isHighlighted =
                      (isMainRow &&
                        row.original.changedOrderFields &&
                        row.original.changedOrderFields.find(
                          (field) => mapKey(field) === cell.column.id
                        )) || // 메인로우의 changedOrderFields
                      (subRowChangedFields && subRowChangedFields[row.index]); // 서브로우의 changedItemFields와 일치하는 셀

                    // 커스텀 스타일 (변경된 셀에 하이라이트 적용)
                    return (
                      <Styled.TdCell
                        $disActivePad={cell.column.id === 'NO'}
                        key={cell.id}
                        {...{
                          className: 'td',
                          style: {
                            // 해당 셀이 변경되었으면 배경 색을 노란색으로 하이라이트
                            backgroundColor: isMainRow
                              ? isHighlighted
                                ? 'rgb(253 255 85 / 26%)'
                                : // : '#3535350a'
                                  theme.colors.white_odd
                              : 'transparent',
                            width: `calc(var(--header-${cell.column.id}-size) * 1px)`, // 셀 크기
                          },
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Styled.TdCell>
                    );
                  })}
                </Styled.TdDiv>
              </>
            ))}

          {/* {table.getRowModel().rows.map((row) => (
            <>
              <Styled.TdDiv
                {...{
                  key: row.id,
                  style: {
                    backgroundColor: row.original.updateFlag
                      ? theme.colors.lime
                      : 'transparent',
                  },
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  // 특정 로직으로 row 정보 확인
                  const orderHighlightCell =
                    (row.original.changedOrderFields &&
                      row.original.changedOrderFields.length > 0 &&
                      row.original.changedOrderFields.map((key) => key)) ||
                    null;

                  const isHighlightedItem =
                    (row.original.subRows &&
                      row.original.subRows.some(
                        (subrow) =>
                          subrow.changedItemField &&
                          subrow.changedItemField.length > 0
                      ) &&
                      row.original.subRows
                        .map((subRow) =>
                          subRow.changedItemField?.map((key) => key)
                        )
                        .filter((item) => item !== undefined && item !== null)
                        .flat()) ||
                    null;

                  const isMainRow = row.depth === 0;

                  const key = cell.column.id;

                  // const customStyle = isHighlighted
                  //   ? { backgroundColor: 'yellow' } // 조건에 맞으면 커스텀 스타일
                  //   : {}; // 기본 스타일

                  return (
                    <Styled.TdCell
                      {...{
                        key: cell.id,
                        className: 'td',
                        style: {
                          width: `calc(var(--header-${cell.column.id}-size) * 1px)`,
                        },
                      }}
                    >
                      {flexRender(
                        // cell.column.columnDef.aggregatedCell ??
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Styled.TdCell>
                  );
                })}
              </Styled.TdDiv>
            </>
          ))} */}
        </Styled.TableDiv>
      </Styled.TableContainer>
    </>
  );
};

const PeriodContainer = styled.div`
  max-width: 800px;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const PeriodRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-items: stretch;

  &:not(:last-child) {
    border-bottom: 1px solid #b8b8b8;
  }
`;
const RowLabel = styled.div`
  flex: 0 0 120px;
  background-color: #d9d9d9;
  font-size: 15px;
  align-content: center;
  text-align: center;
  padding: 4px 3px;
  font-weight: 700;
`;

const RowContent = styled.div`
  flex-grow: 1;
  padding: 4px 10px;
  background-color: white;
`;

const DateInput = styled.input`
  width: 100%;
  color: #595b60;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  box-sizing: border-box;
  border: none;
  cursor: pointer;
`;

const DropdownBase = styled.div<{ $show?: boolean }>`
  font-weight: 500;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
  position: absolute;
  background-color: white;
  left: 100%;
  margin-left: 15px;
  padding: 5px;
  gap: 6px;
  width: 150px;
  top: 0px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(71, 71, 71, 0.678);
  transform: ${({ $show }) => ($show ? 'scale(1)' : 'scale(0)')};
  transform-origin: top left;
  transition: transform 0.3s ease, width 0.3s ease, height 0.3s ease;
`;

const DropdownItem = styled.div<{ included: boolean }>`
  width: 100%;
  padding: 4px 1px;
  ${({ included }) =>
    included
      ? css`
          background-color: ${({ theme }) => theme.colors.lime};
        `
      : css`
          cursor: pointer;
          &:hover {
            background-color: ${({ theme }) => theme.colors.lightGray};
          }
        `}
`;
